/** The approved Timeline / Created date contract shared by CRM and Audiences. */
export const CRM_CREATED_DATE_CONDITIONS = [
  "is_within",
  "before",
  "after",
  "between",
] as const;
export type CrmCreatedDateCondition =
  (typeof CRM_CREATED_DATE_CONDITIONS)[number];

export const CRM_CREATED_DATE_PERIODS = [
  { value: "last_hour", label: "Last hour", hours: -1 },
  { value: "last_24_hours", label: "Last 24 hours", hours: -24 },
  { value: "last_48_hours", label: "Last 48 hours", hours: -48 },
  { value: "last_7_days", label: "Last 7 days", hours: -168 },
  { value: "last_30_days", label: "Last 30 days", hours: -720 },
  { value: "last_60_days", label: "Last 60 days", hours: -1440 },
  { value: "last_90_days", label: "Last 90 days", hours: -2160 },
  { value: "next_3_days", label: "Next 3 days", hours: 72 },
  { value: "next_7_days", label: "Next 7 days", hours: 168 },
  { value: "next_30_days", label: "Next 30 days", hours: 720 },
] as const;

/**
 * Not in Kate's filter taxonomy, so the general date pickers leave it out:
 * "Next 3 days" is the built-in Trial ending tabs' window (FB-28). The Trial
 * ending date picker alone also offers it (OD-9, CrmTaxonomyDateValueAdapter).
 */
export const CRM_BUILT_IN_ONLY_DATE_PERIODS: readonly string[] = [
  "next_3_days",
];
/** The periods a person can choose in a date picker. */
export const CRM_PICKER_DATE_PERIODS = CRM_CREATED_DATE_PERIODS.filter(
  ({ value }) => !CRM_BUILT_IN_ONLY_DATE_PERIODS.includes(value),
);

export interface CrmCalendarDateSelection {
  date: string;
  endDate?: string;
  timeZone: string;
}

/** Do not let Date.parse silently normalize impossible dates (e.g. 31 February). */
export const isCrmCalendarDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^(?!0000)\d{4}-\d{2}-\d{2}$/u.test(value))
    return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
};

export const normalizeCrmCalendarTimeZone = (value: unknown): string | null => {
  if (typeof value !== "string" || !value || value.length > 100) return null;
  try {
    return new Intl.DateTimeFormat("en", { timeZone: value }).resolvedOptions()
      .timeZone;
  } catch {
    return null;
  }
};

export const CRM_RELATIVE_CALENDAR_DATE_PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
] as const;
export type CrmRelativeCalendarDateSelection = {
  period: "today" | "yesterday";
  timeZone: string;
};

/** A relative calendar period always carries its saved zone, never a guessed one. */
export const readCrmRelativeCalendarDateDraft = (
  value: unknown,
): CrmRelativeCalendarDateSelection | null => {
  if (typeof value !== "string" || value.length > 400) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return null;
    const draft = parsed as Record<string, unknown>;
    if (Object.keys(draft).some((key) => !["period", "timeZone"].includes(key)))
      return null;
    const timeZone = normalizeCrmCalendarTimeZone(draft.timeZone);
    return timeZone &&
      (draft.period === "today" || draft.period === "yesterday")
      ? { period: draft.period, timeZone }
      : null;
  } catch {
    return null;
  }
};

/** Reads a draft without discarding its first date while the end date is entered. */
export const readCrmCalendarDateDraft = (
  value: unknown,
): CrmCalendarDateSelection | null => {
  if (typeof value !== "string" || value.length > 400) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return null;
    const draft = parsed as Record<string, unknown>;
    if (
      Object.keys(draft).some(
        (key) => !["date", "endDate", "timeZone"].includes(key),
      ) ||
      typeof draft.date !== "string" ||
      (draft.endDate !== undefined && typeof draft.endDate !== "string")
    )
      return null;
    const timeZone = normalizeCrmCalendarTimeZone(draft.timeZone);
    if (!timeZone) return null;
    return {
      date: draft.date,
      ...(draft.endDate !== undefined ? { endDate: draft.endDate } : {}),
      timeZone,
    };
  } catch {
    return null;
  }
};

/** Canonical wire order makes equivalent saved definitions have the same digest. */
export const normalizeCrmCreatedDateValue = (
  condition: string,
  value: unknown,
  /** Built-in-only periods (Next 3 days) are valid for Trial ending only. */
  options: { allowBuiltInOnly?: boolean } = {},
): string | null => {
  if (condition === "is_within") {
    const period = typeof value === "string" ? value.trim() : "";
    const rolling =
      CRM_CREATED_DATE_PERIODS.some((entry) => entry.value === period) &&
      (options.allowBuiltInOnly === true ||
        !CRM_BUILT_IN_ONLY_DATE_PERIODS.includes(period))
        ? period
        : null;
    const calendar = readCrmRelativeCalendarDateDraft(value);
    return rolling ?? (calendar ? JSON.stringify(calendar) : null);
  }
  if (
    condition !== "before" &&
    condition !== "after" &&
    condition !== "between"
  )
    return null;
  const draft = readCrmCalendarDateDraft(value);
  if (!draft || !isCrmCalendarDate(draft.date)) return null;
  if (condition === "between") {
    if (!isCrmCalendarDate(draft.endDate) || draft.date > draft.endDate)
      return null;
  } else if (draft.endDate !== undefined) return null;
  return JSON.stringify(draft);
};

const sourceTimestamp = (value: unknown): number | null => {
  if (value instanceof Date)
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (
    typeof value !== "string" ||
    !isCrmCalendarDate(value.slice(0, 10)) ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/u.test(
      value,
    )
  )
    return null;
  const instant = Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
};

const calendarDateFormatter = (timeZone: string) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    calendar: "gregory",
    numberingSystem: "latn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return (instant: number): string => {
    const parts = formatter.formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((entry) => entry.type === type)?.value ?? "";
    return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
  };
};

/**
 * The calendar-date fields (Created date, Trial ending date) share one value
 * grammar; only Trial ending accepts the built-in-only Next 3 days (FB-28).
 */
export const normalizeCrmCalendarDateFieldValue = (
  field: string,
  condition: string,
  value: unknown,
): string | null =>
  normalizeCrmCreatedDateValue(condition, value, {
    allowBuiltInOnly: field === "trial_ending_date",
  });

/**
 * Compile once at the list/evaluation boundary. Calendar predicates compare dates
 * in the saved zone, not UTC midnights or guessed 24-hour days; rolling predicates
 * retain this one evaluation instant rather than drifting between records.
 */
export const compileCrmCreatedDateFilter = (
  condition: string,
  value: unknown,
  nowMs: number,
  options: { allowBuiltInOnly?: boolean } = {},
): ((createdAt: unknown) => boolean) => {
  const normalized = normalizeCrmCreatedDateValue(condition, value, options);
  if (normalized === null || !Number.isFinite(nowMs)) return () => false;
  if (condition === "is_within") {
    const period = CRM_CREATED_DATE_PERIODS.find(
      (entry) => entry.value === normalized,
    );
    if (!period) {
      const calendar = readCrmRelativeCalendarDateDraft(normalized);
      if (!calendar) return () => false;
      const formatDate = calendarDateFormatter(calendar.timeZone);
      const today = formatDate(nowMs);
      // Subtract one civil date, not 24 hours from the evaluation instant.
      const targetDate =
        calendar.period === "today"
          ? today
          : new Date(Date.parse(`${today}T00:00:00Z`) - 86_400_000)
              .toISOString()
              .slice(0, 10);
      return (createdAt) => {
        const instant = sourceTimestamp(createdAt);
        return (
          instant !== null &&
          instant <= nowMs &&
          formatDate(instant) === targetDate
        );
      };
    }
    const endpoint = nowMs + period.hours * 60 * 60 * 1000;
    return (createdAt) => {
      const instant = sourceTimestamp(createdAt);
      return (
        instant !== null &&
        (period.hours < 0
          ? instant >= endpoint && instant <= nowMs
          : instant > nowMs && instant <= endpoint)
      );
    };
  }
  const selection = readCrmCalendarDateDraft(normalized);
  if (!selection) return () => false;
  const formatDate = calendarDateFormatter(selection.timeZone);
  return (createdAt) => {
    const instant = sourceTimestamp(createdAt);
    if (instant === null) return false;
    const date = formatDate(instant);
    return condition === "before"
      ? date < selection.date
      : condition === "after"
        ? date > selection.date
        : date >= selection.date && date <= (selection.endDate ?? "");
  };
};

/** Plain summaries never expose the serialized calendar operand to staff. */
export const formatCrmCreatedDateValue = (value: unknown): string => {
  const period = CRM_CREATED_DATE_PERIODS.find(
    (entry) => entry.value === value,
  );
  if (period) return period.label;
  const calendar = readCrmRelativeCalendarDateDraft(value);
  if (calendar) {
    const label =
      CRM_RELATIVE_CALENDAR_DATE_PERIODS.find(
        (entry) => entry.value === calendar.period,
      )?.label ?? calendar.period;
    return `${label} (${calendar.timeZone.replaceAll("_", " ")})`;
  }
  const draft = readCrmCalendarDateDraft(value);
  if (
    !draft ||
    !isCrmCalendarDate(draft.date) ||
    (draft.endDate !== undefined &&
      (!isCrmCalendarDate(draft.endDate) || draft.endDate < draft.date))
  )
    return "Choose valid dates";
  const format = (date: string) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(`${date}T00:00:00Z`));
  return `${format(draft.date)}${draft.endDate ? ` – ${format(draft.endDate)}` : ""} (${draft.timeZone.replaceAll("_", " ")})`;
};
